import axios from "axios";
import { API_BASE } from "../Utils/constant";
import { retrieve, save, remove } from "../helpers/StorageHelper";
import { AUTH_TOKEN, USER_DATA } from "../constants/localStorageKeys";
import { getUserTimezone } from "../helpers/TimezoneHelper";
import useAuthStore from "../zustand-stores/AuthStore";

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const api = axios.create({
  baseURL: API_BASE,
});

// we'll use interceptor for sending token in api

api.interceptors.request.use(
  (config) => {
    const token = retrieve(AUTH_TOKEN);
    const accessToken = token;
    if (accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    // Add timezone header to all requests
    if (config.headers) {
      config.headers["X-Timezone"] = getUserTimezone(); //changed to X-Timezone for nginx compatible
    }

    return config;
  },
  (err) => {
    return err;
  },
);

api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const status = error.response?.status;
    const originalRequest = error.config;

    if (status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const currentToken = retrieve(AUTH_TOKEN);

      return new Promise((resolve, reject) => {
        axios({
          method: "post",
          url: `${API_BASE}/refresh`,
          data: {
            token: currentToken,
          },
          headers: {
            Authorization: `Bearer ${currentToken}`,
            Accept: "application/json",
          },
        })
          .then((res) => {
            // Check for potential nested data structure in different backend helpers
            const newToken = res.data?.data?.token || res.data?.token;

            if (!newToken) {
              throw new Error("No token found in refresh response");
            }

            save(AUTH_TOKEN, newToken);
            useAuthStore.getState().setAuthSession(newToken);

            // Update Authorization header for the original request
            if (originalRequest.headers.set) {
              originalRequest.headers.set(
                "Authorization",
                `Bearer ${newToken}`,
              );
            } else {
              originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
            }

            processQueue(null, newToken);
            resolve(api(originalRequest));
          })
          .catch((err) => {
            processQueue(err, null);
            // Clear the auth token from localStorage
            remove(AUTH_TOKEN);
            remove(USER_DATA);
            // Redirect to login page
            if (window.location.pathname !== "/login") {
              window.location.href = "/login";
            }
            reject(err);
          })
          .finally(() => {
            isRefreshing = false;
          });
      });
    }

    // Do not hard-redirect here; allow callers/route guards to handle auth state
    return Promise.reject(error);
  },
);

export { api };
